/**
 * An `app_card` (slice 6i §7, Paper E1, E2): the app the agent published in
 * this chat, as a doc's card is (slice 2d spec §8). Live from the Apps list
 * when the person can see it (its title, description, version and sharing);
 * "This app was deleted." when it's gone; nothing to open when it's someone
 * else's they can't see.
 */
import { Link } from "@crew/ui/components/link";
import { useApps, type AppItem } from "./api";
import { AppTile } from "./apps-page";

const SHARED: Record<AppItem["sharing"], string> = {
  owner: "just you",
  people: "shared",
  crew: "the whole crew",
};

export function AppChatCard({
  appId,
  fallbackTitle,
  version,
  size,
}: {
  appId: string;
  fallbackTitle: string;
  version: number;
  size: "md" | "sm";
}) {
  const apps = useApps();
  const app = apps.data?.find((a) => a.id === appId);
  const gone = apps.isSuccess && !app;
  return (
    <div
      data-slot="app-card"
      data-app-id={appId}
      className={`flex w-full items-center gap-3 self-start rounded-xl border border-border bg-card px-3.5 py-3 ${size === "sm" ? "max-w-full" : "max-w-[460px]"}`}
    >
      <AppTile id={appId} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-body font-medium text-foreground">
          {app?.title ?? fallbackTitle}
        </span>
        {gone ? (
          <span className="text-meta text-muted-foreground">
            This app was deleted, or isn't shared with you.
          </span>
        ) : (
          <>
            {app?.description ? (
              <span className="line-clamp-2 text-meta text-copy">{app.description}</span>
            ) : null}
            <span data-app-card-footer className="text-xs text-muted-foreground">
              App · version {app?.version ?? version}
              {app ? ` · ${app.mine ? SHARED[app.sharing] : `${app.owner.name}'s`}` : ""}
            </span>
          </>
        )}
      </span>
      {gone ? null : (
        <Link
          href={`/apps/${appId}`}
          className="flex h-8 shrink-0 items-center rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground"
        >
          Open
        </Link>
      )}
    </div>
  );
}
